import React from 'react';
import SideFilterMultiSelect from './components/SideFilterMultiSelect/SideFilterMultiSelect';
import SideFilterNumericRange from './components/SideFilterNumericRange/SideFilterNumericRange';
import SideFilterStars from './components/SideFilterStars/SideFilterStars';
import { OptionalFilter, SideFilterType } from './interface';
import SideFilterTime from './components/SideFilterTime/SideFilterTime';
import SideFilterBoolean from './components/SideFilterBoolean/SideFilterBoolean';
import SideFilterTree from './components/SideFilterTree/SideFilterTree';
import SideFilterName from './components/SideFilterName/SideFilterName';
import SideFilterSortBy from './components/SideFilterSortBy/SideFilterSortBy';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
// import { SortByNames } from './SideFilters';

interface DynamicSorting {
  name: string;
  label: string;
}

interface Props {
  optional_filters?: OptionalFilter[];
  starsGreaterThanRatingFilter?: boolean;
  pricingMaxLimitAndPlusFilter?: number | undefined;
  withModalScopeParams?: boolean;
  isLoading?: boolean;
  hasPartiallyLoaded?: boolean;
  additionalPrice?: number;
  freeCancellationTooltipText?: string;
  dynamicSorting?: DynamicSorting[];
  filterEntityType?: 'dynamic_flights' | 'bundle_flights';
  isReturnFlight?: boolean;
  showPricePerPax?: boolean;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
}

const SideFiltersContent = ({
  optional_filters,
  starsGreaterThanRatingFilter,
  pricingMaxLimitAndPlusFilter,
  withModalScopeParams,
  isLoading,
  hasPartiallyLoaded,
  additionalPrice = 0,
  freeCancellationTooltipText,
  dynamicSorting,
  filterEntityType,
  isReturnFlight,
  showPricePerPax,
  localSideFilters,
  setLocalSideFilters
}: Props) => {
  const renderSideFilter = (filter: OptionalFilter) => {
    switch (filter.type) {
      case SideFilterType.MultiSelect:
        return (
          <SideFilterMultiSelect
            filter={filter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            additionalPrice={additionalPrice}
            freeCancellationTooltipText={
              freeCancellationTooltipText && filter.attr === 'freeCancellation'
                ? freeCancellationTooltipText
                : undefined
            }
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.Nights:
        return (
          <SideFilterMultiSelect
            filter={filter}
            isNights
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            additionalPrice={additionalPrice}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.Stars:
        return (
          <SideFilterStars
            filter={filter}
            starsGreaterThanRatingFilter={starsGreaterThanRatingFilter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            additionalPrice={additionalPrice}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.NumericRange:
        return (
          <SideFilterNumericRange
            filter={filter}
            pricingMaxLimitAndPlusFilter={pricingMaxLimitAndPlusFilter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            additionalPrice={filter.attr === 'distance' ? 0 : additionalPrice}
            showPricePerPax={showPricePerPax}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.Hours:
        return (
          <SideFilterTime
            filter={filter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.Boolean:
        return (
          <SideFilterBoolean
            filter={filter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.Tree:
        return (
          <SideFilterTree
            filter={filter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.FreeText:
        return (
          <SideFilterName
            filter={filter}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
      case SideFilterType.SelectOne:
        return (
          <SideFilterSortBy
            filter={filter}
            hideApplyButton={true}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        );
    }
  };

  const getSortByAttr = ({
    isDynamicFlight,
    attrPrefix,
  }: {
    isDynamicFlight?: boolean;
    attrPrefix?: string;
  }) => {
    if (isDynamicFlight) {
      return attrPrefix + 'sortBy';
    }
    return 'sortBy';
  };

  return (
    // <>
    <SpinLoader isLoading={!!hasPartiallyLoaded} onTop>
      {dynamicSorting && (
        <div className={`filter`}>
          <SideFilterSortBy
            filter={{
              type: SideFilterType.SelectOne,
              attr: getSortByAttr({
                isDynamicFlight: filterEntityType === 'dynamic_flights',
                attrPrefix: isReturnFlight ? 'f2_' : 'f1_',
              }),
              options: dynamicSorting?.map((sortOpt) => sortOpt.name),
            }}
            hideApplyButton={true}
            withModalScopeParams={withModalScopeParams}
            isLoading={isLoading}
            localSideFilters={localSideFilters}
            setLocalSideFilters={setLocalSideFilters}
          />
        </div>
      )}

      {optional_filters?.map((optional_filter, key) => {
        return (
          <div
            key={key}
            className={`filter ${
              optional_filter.type === SideFilterType.Boolean
                ? 'small-paddings'
                : ''
            }`}
          >
            {renderSideFilter(optional_filter as OptionalFilter)}
          </div>
        );
      })}
    </SpinLoader>
    // </>
  );
};

export default SideFiltersContent;
